import { useEffect, useMemo, useState } from "react";
import { X, RefreshCw, Check, AlertTriangle } from "lucide-react";
import clsx from "clsx";
import { ShowToast } from "../../../components/toast";
import { useSyncFromStripe } from "../hooks";
import { BillingReconciliation, BulkReconciliation, ReconciliationStatus } from "../types";

interface SyncAllModalProps {
  data: BulkReconciliation;
  onClose: () => void;
  onApplied?: () => void; // fired after a drift is applied, to refresh the table
}

const pctRate = (r: number | null | undefined) =>
  r == null ? "—" : `${(r * 100).toFixed(1)}%`;

const STATUS_BADGE: Record<ReconciliationStatus, { label: string; className: string }> = {
  in_sync: { label: "In sync", className: "bg-green-100 text-green-800 border-green-300" },
  drift: { label: "Drift", className: "bg-amber-100 text-amber-800 border-amber-300" },
  needs_review: { label: "Needs review", className: "bg-amber-100 text-amber-800 border-amber-300" },
  no_billing_config: { label: "No config", className: "bg-gray-100 text-gray-700 border-gray-300" },
  no_stripe_customer: { label: "No Stripe customer", className: "bg-gray-100 text-gray-700 border-gray-300" },
  stripe_error: { label: "Stripe error", className: "bg-red-100 text-red-800 border-red-300" },
};

// Action-needed rows float to the top; in_sync sinks to the bottom.
const STATUS_SORT: Record<ReconciliationStatus, number> = {
  drift: 0,
  needs_review: 1,
  stripe_error: 2,
  no_stripe_customer: 3,
  no_billing_config: 4,
  in_sync: 5,
};

export function SyncAllModal({ data, onClose, onApplied }: SyncAllModalProps) {
  const [rows, setRows] = useState<BillingReconciliation[]>(data.results);
  // Track per-row in-flight applies so one apply doesn't disable every button.
  const [applyingIds, setApplyingIds] = useState<Set<string>>(new Set());
  const syncMutation = useSyncFromStripe();

  // Re-seed rows if the parent hands us a fresh reconciliation run.
  useEffect(() => {
    setRows(data.results);
  }, [data]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => STATUS_SORT[a.status] - STATUS_SORT[b.status]),
    [rows],
  );

  const summary = useMemo(
    () => ({
      total: rows.length,
      inSync: rows.filter((r) => r.status === "in_sync").length,
      drift: rows.filter((r) => r.status === "drift").length,
      needsReview: rows.filter(
        (r) =>
          r.status === "needs_review" ||
          r.status === "no_billing_config" ||
          r.status === "no_stripe_customer",
      ).length,
      error: rows.filter((r) => r.status === "stripe_error").length,
    }),
    [rows],
  );

  const handleApply = (userId: string) => {
    setApplyingIds((prev) => new Set(prev).add(userId));
    syncMutation.mutate(userId, {
      onSuccess: (result) => {
        // Always reflect the post-sync server truth in the row…
        setRows((prev) =>
          prev.map((r) =>
            r.userId === userId
              ? { ...result.reconciliation, label: r.label }
              : r,
          ),
        );
        // …but only treat it as an applied change when the backend wrote one.
        if (result.applied) {
          ShowToast({ type: "success", message: "App commission rate synced from Stripe" });
          onApplied?.();
        } else {
          ShowToast({
            type: "error",
            message: "Rate not applied — re-check this customer (its config may have changed).",
          });
        }
      },
      onSettled: () =>
        setApplyingIds((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        }),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-primary rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-secondary shrink-0">
          <div>
            <h3 className="text-base font-semibold text-content-primary">Stripe Reconciliation</h3>
            <p className="text-xs text-content-tertiary mt-0.5">
              App commission rates vs Stripe discounts.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-content-tertiary hover:text-content-primary hover:bg-surface-secondary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-border-secondary shrink-0 text-xs">
          <span className="text-content-tertiary">{summary.total} checked</span>
          <span className="px-2 py-0.5 rounded-full border bg-green-100 text-green-800 border-green-300">
            {summary.inSync} in sync
          </span>
          <span className="px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300">
            {summary.drift} drift
          </span>
          <span className="px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300">
            {summary.needsReview} need review
          </span>
          <span className="px-2 py-0.5 rounded-full border bg-red-100 text-red-800 border-red-300">
            {summary.error} errors
          </span>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          {sorted.length === 0 ? (
            <p className="text-sm text-content-tertiary text-center py-10">
              No configured billing customers with a linked Stripe customer.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-secondary text-content-tertiary text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-medium px-6 py-2.5">Customer</th>
                  <th className="text-right font-medium px-3 py-2.5">App</th>
                  <th className="text-right font-medium px-3 py-2.5">Stripe</th>
                  <th className="text-left font-medium px-3 py-2.5">Status</th>
                  <th className="text-right font-medium px-6 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const badge = STATUS_BADGE[r.status];
                  const isApplying = applyingIds.has(r.userId);
                  return (
                    <tr key={r.userId} className="border-t border-border-secondary">
                      <td className="px-6 py-3">
                        <div className="font-medium text-content-primary truncate max-w-[220px]">
                          {r.label || r.userId}
                        </div>
                        {r.warnings.length > 0 && (
                          <div className="text-[11px] text-content-tertiary mt-0.5 flex items-start gap-1">
                            <AlertTriangle className="w-3 h-3 text-warning-dark mt-0.5 shrink-0" />
                            <span className="truncate max-w-[260px]" title={r.warnings.join("; ")}>
                              {r.warnings[0]}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-content-secondary">
                        {pctRate(r.appCommissionRate)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-content-secondary">
                        {r.status === "drift"
                          ? pctRate(r.suggestedRate)
                          : r.stripeDiscountRates.length
                            ? pctRate(r.stripeDiscountRates[0])
                            : "—"}
                      </td>
                      <td className="px-3 py-3">
                        <span className={clsx("px-2 py-0.5 rounded-full border text-xs whitespace-nowrap", badge.className)}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right">
                        {r.status === "drift" ? (
                          <button
                            onClick={() => handleApply(r.userId)}
                            disabled={isApplying}
                            className="inline-flex items-center gap-1 text-xs text-white bg-primary hover:bg-primary-hover rounded-md px-2.5 py-1.5 font-medium disabled:opacity-50"
                          >
                            <RefreshCw className={clsx("w-3 h-3", isApplying && "animate-spin")} />
                            {isApplying ? "Applying…" : `Apply ${pctRate(r.suggestedRate)}`}
                          </button>
                        ) : r.status === "in_sync" ? (
                          <Check className="w-4 h-4 text-success inline-block" />
                        ) : (
                          <span className="text-xs text-content-tertiary">Manual</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
